/**
 * Bulk Add Clients — paste CSV of up to 50 client rows + preview + commit.
 *
 * CSV columns (first row is header, order flexible):
 *   name, contact_name, contact_email, phone, address_line1, [address_line2],
 *   city, state, postal_code, [plan], [billing_mode]
 *
 * We POST once with dry_run=true to show a preview table, then once more
 * without dry_run after the firm clicks Create. Per-row errors render inline
 * so a firm can fix bad phone numbers / dupes before committing.
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Upload, Loader2, CheckCircle2, AlertCircle, Users, Copy, Layers, X,
} from "lucide-react";

const SAMPLE = `name,contact_name,contact_email,phone,address_line1,city,state,postal_code,plan,billing_mode
Acme Co,Jane Doe,jane@acme.co,(415) 555-0100,500 Market St,San Francisco,CA,94103,trial,client_trial
Beta Inc,Jim Smith,jim@beta.co,(415) 555-0200,1 Industrial Pkwy,Elizabeth,NJ,07201,pro,firm_pays`;

/** Minimal CSV parser — handles quoted fields and commas inside quotes. We
 * don't need the full RFC; our known headers are simple. */
function parseCsv(text) {
  const lines = (text || "").trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = splitLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows = lines.slice(1).map((ln) => {
    const cells = splitLine(ln);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
    return obj;
  });
  return { headers, rows };
}

function splitLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === "," && !inQ) {
      out.push(cur); cur = "";
    } else { cur += c; }
  }
  out.push(cur);
  return out;
}

const STATUS_META = {
  created:   { tone: "#065F46", bg: "#ECFDF5", label: "Created" },
  skipped:   { tone: "#1E40AF", bg: "#EFF6FF", label: "Will create" },
  error:     { tone: "#991B1B", bg: "#FEF2F2", label: "Error" },
  duplicate: { tone: "#92400E", bg: "#FEF3C7", label: "Duplicate" },
};

export default function BulkAddClientsModal({ open, onClose, onCreated }) {
  const [raw, setRaw] = useState(SAMPLE);
  const [preview, setPreview] = useState(null); // null | {total,created,skipped,errored,results[]}
  const [running, setRunning] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/firm/templates").then((r) => {
      setTemplates(r.data || []);
      const def = (r.data || []).find((t) => t.is_default);
      if (def) setTemplateId(def.id);
    }).catch(() => setTemplates([]));
  }, [open]);

  const close = () => {
    if (running) return;
    setPreview(null);
    setRaw(SAMPLE);
    onClose();
  };

  const run = async (dry) => {
    setRunning(true);
    try {
      const { rows } = parseCsv(raw);
      if (!rows.length) throw new Error("Paste at least one CSV row below the header");
      const payload = {
        dry_run: dry,
        template_id: templateId || undefined,
        rows,
      };
      const { data } = await api.post("/firm/clients/bulk", payload);
      setPreview(data);
      if (!dry) {
        toast.success(`Created ${data.created} of ${data.total} client workspaces`);
        if (data.created > 0) onCreated?.(data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ")
        : (typeof detail === "string" ? detail : (e.message || "Failed"));
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="bulk-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users size={16} /> Bulk add client workspaces
          </DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">
            Paste a CSV with up to 50 client rows below. We'll validate every row and show
            a preview before anything's created.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {templates.length > 0 && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1 flex items-center gap-1.5">
                <Layers size={11} /> Template applied to every row
              </label>
              <select
                value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                data-testid="bulk-template-id"
                className="w-full px-3 py-2 border text-sm bg-white" style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">No template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.categories?.length || 0} categories){t.is_default ? " · default" : ""}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">CSV</label>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(SAMPLE); toast.success("Sample CSV copied"); }}
                className="text-[11px] text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)] inline-flex items-center gap-1"
              >
                <Copy size={10} /> Copy sample
              </button>
            </div>
            <textarea
              value={raw}
              onChange={(e) => { setRaw(e.target.value); setPreview(null); }}
              data-testid="bulk-csv-textarea"
              rows={10}
              spellCheck={false}
              className="w-full px-3 py-2 border text-xs font-mono-fin"
              style={{ borderColor: "var(--ll-border)" }}
            />
            <div className="text-[10px] text-[var(--ll-ink-muted)] mt-1">
              Columns: name, contact_name, contact_email, phone (US format), address_line1, city, state, postal_code, plan, billing_mode
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button" onClick={() => run(true)} disabled={running}
              data-testid="bulk-preview-btn"
              className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] disabled:opacity-60 flex items-center gap-1.5"
              style={{ borderColor: "var(--ll-border)" }}
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Preview
            </button>
            {preview && preview.errored === 0 && (
              <button
                type="button" onClick={() => run(false)} disabled={running}
                data-testid="bulk-commit-btn"
                className="text-sm px-4 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#0b5d9e] disabled:opacity-60 flex items-center gap-1.5"
              >
                {running ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                Create {preview.total} workspace{preview.total === 1 ? "" : "s"}
              </button>
            )}
            {preview && preview.errored > 0 && (
              <span className="text-xs text-[#991B1B] flex items-center gap-1.5" data-testid="bulk-blocked-msg">
                <AlertCircle size={12} /> Fix the {preview.errored} errored row{preview.errored === 1 ? "" : "s"} before creating
              </span>
            )}
          </div>

          {preview && (
            <div className="border" style={{ borderColor: "var(--ll-border)" }} data-testid="bulk-preview-table">
              <div className="px-3 py-2 border-b text-xs flex items-center gap-3 flex-wrap" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-sidebar)" }}>
                <span><strong>{preview.total}</strong> rows</span>
                <span className="text-[#065F46]"><strong>{preview.created || preview.skipped}</strong> {preview.created > 0 ? "created" : "valid"}</span>
                {preview.errored > 0 && <span className="text-[#991B1B]"><strong>{preview.errored}</strong> errors</span>}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
                    <th className="px-3 py-1.5 text-left">#</th>
                    <th className="px-3 py-1.5 text-left">Name</th>
                    <th className="px-3 py-1.5 text-left">Email</th>
                    <th className="px-3 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.results.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.skipped;
                    return (
                      <tr key={r.index} className="border-t" style={{ borderColor: "var(--ll-border)" }} data-testid={`bulk-row-${r.index}`}>
                        <td className="px-3 py-1.5 text-[var(--ll-ink-muted)]">{r.index + 1}</td>
                        <td className="px-3 py-1.5">{r.name || "—"}</td>
                        <td className="px-3 py-1.5">{r.contact_email || "—"}</td>
                        <td className="px-3 py-1.5">
                          <span className="inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-widest" style={{ background: meta.bg, color: meta.tone }}>
                            {meta.label}
                          </span>
                          {r.error && <span className="ml-2 text-[11px] text-[#991B1B]">{r.error}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-3 border-t mt-3" style={{ borderColor: "var(--ll-border)" }}>
          <button
            type="button" onClick={close} disabled={running}
            data-testid="bulk-close-btn"
            className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] disabled:opacity-50 flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <X size={13} /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
